/**
 * Roster import — the Firebase half (Beta-1 plan P4-16). Reads the uploaded file (CSV or XLSX)
 * in the browser, hands the pure mapping to `utils/rosterImport.js`, and creates accounts in
 * small batches through `hugoCreateOrgUser` (P4-09 fields, Notify / Quiet). WF7b (AI column
 * mapping, P7-08) plugs in through `mapColumnsWithAi` when its webhook is configured; otherwise
 * the local synonym mapper is used.
 */
import * as XLSX from 'xlsx';
import { parseCsv, mapColumns } from '../assistants/drew/utils/rosterImport';
import { provisionOrgUser } from './orgUserService';
import { logActivity } from '../utils/firestore';

const ROSTER_MAP_URL = import.meta.env.VITE_N8N_WEBHOOK_ROSTER_MAP || null;
const AUTH_HEADER = import.meta.env.VITE_N8N_WEBHOOK_BASE_AUTH_HEADER || null; // P7-12 shared secret (n8n Header Auth)

/** { headers, rows } from a File (.csv / .xlsx / .xls). */
export async function readRosterFile(file) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (ext === 'csv' || ext === 'txt') return parseCsv(await file.text());
  if (ext === 'xlsx' || ext === 'xls') {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
    const nonEmpty = aoa.filter((r) => r.some((v) => String(v).trim() !== ''));
    const headers = (nonEmpty.shift() || []).map((h) => String(h).trim());
    return { headers, rows: nonEmpty.map((r) => headers.map((_, i) => String(r[i] ?? '').trim())) };
  }
  throw new Error('Upload a CSV or Excel (.xlsx) file — or download the template first.');
}

/** AI column mapping (WF7b) when configured, else the local synonym mapper. */
export async function mapColumnsWithAi(headers, sampleRows = []) {
  const local = mapColumns(headers);
  if (!ROSTER_MAP_URL) return { mapping: local, source: 'local' };
  try {
    const res = await fetch(ROSTER_MAP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(AUTH_HEADER ? { Authorization: AUTH_HEADER } : {}) },
      body: JSON.stringify({ headers, sample: sampleRows.slice(0, 5) }),
    });
    if (!res.ok) throw new Error(`WF7b ${res.status}`);
    const data = await res.json();
    const mapping = data?.mapping && typeof data.mapping === 'object' ? data.mapping : null;
    return mapping ? { mapping: { ...local, ...mapping }, source: 'ai' } : { mapping: local, source: 'local' };
  } catch (err) {
    console.warn('Roster AI mapping unavailable, using local mapper:', err.message);
    return { mapping: local, source: 'local' };
  }
}

/**
 * Create accounts for the ready rows, `batchSize` at a time. `onProgress({ done, total, row, result|error })`.
 * Never throws for a single failed row — the summary lists them.
 */
export async function createAccountsFromRoster({ organizationId, rows, notify, user, onProgress = () => {}, batchSize = 5 }) {
  const results = [];
  for (let i = 0; i < rows.length; i += batchSize) {
    const batch = rows.slice(i, i + batchSize);
    // eslint-disable-next-line no-await-in-loop
    const settled = await Promise.allSettled(
      batch.map((row) =>
        provisionOrgUser({
          firstName: row.firstName,
          lastName: row.lastName,
          displayName: row.displayName,
          title: row.title,
          email: row.email,
          role: row.role,
          scopeId: row.role === 'site_manager' ? row.siteId : row.role === 'department_lead' ? row.deptId : row.role === 'team_lead' || row.role === 'respondent' ? row.nwtId : null,
          siteId: row.siteId,
          deptId: row.deptId,
          nwtId: row.nwtId,
          notify,
          organizationId,
        })
      )
    );
    settled.forEach((s, j) => {
      const row = batch[j];
      const entry = s.status === 'fulfilled' ? { row, result: s.value } : { row, error: s.reason?.message || 'Failed' };
      results.push(entry);
      onProgress({ done: results.length, total: rows.length, ...entry });
    });
  }
  try {
    await logActivity(user?.uid || 'system', 'roster_imported', 'drift-accumulation', {
      organizationId,
      created: results.filter((r) => r.result?.isNewUser).length,
      linked: results.filter((r) => r.result && !r.result.isNewUser).length,
      failed: results.filter((r) => r.error).length,
      notify,
    });
  } catch {
    /* best-effort */
  }
  return results;
}
